import React from 'react';
import { useLocation, useNavigate, useSearchParams, matchPath } from 'react-router-dom';
import { getLocationReturnPath, navigateBackOrFallback, useNavigateBack } from '../../../hooks/useNavigateBack';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { fetcher } from '../../../services/fetcher';
import { createSparePurchaseOrder, updateSparePurchaseOrder } from '../../../services/mutations';
import {
  Accordion,
  AccordionItem,
  Button,
  Callout,
  Card,
  ChevronLeftIcon,
  DateInput,
  Label,
  NumberInput,
  PlusIcon,
  RefreshIcon,
  Select,
  Textarea,
  TextInput,
  TrashIcon,
} from '../../../components/ui';

import { moduleConfig } from './sparePurchaseOrdersModuleConfig';
import { SparePurchaseOrderExpense, SparePurchaseOrderLine, useSparePurchaseOrder } from './useData';
import { formatCaseParenthetical } from '../requisitions/caseDisplay';

type LineForm = {
  key: string;
  level1Filter: string;
  itemId: string;
  itemVcode: string;
  itemDescr: string;
  unitId: string;
  qty: string;
  unitPrice: string;
  unitTotal: string;
  lineTotal: string;
  discount: string;
  vat: string;
  quotationQty: string;
  itemNotes: string;
};

const newLine = (): LineForm => ({
  key: `line-${Date.now()}-${Math.random()}`,
  level1Filter: '',
  itemId: '',
  itemVcode: '',
  itemDescr: '',
  unitId: '',
  qty: '',
  unitPrice: '',
  unitTotal: '',
  lineTotal: '',
  discount: '',
  vat: '',
  quotationQty: '',
  itemNotes: '',
});

type ExpenseForm = {
  key: string;
  description: string;
  value: string;
  vat: string;
};

const newExpense = (): ExpenseForm => ({
  key: `exp-${Date.now()}-${Math.random()}`,
  description: '',
  value: '',
  vat: '',
});

const asNumber = (value: string): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

const toFixed2String = (value: number): string => value.toFixed(2);
const normalizeTo2Dp = (value: string): string => {
  if (!value || value.trim() === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? toFixed2String(n) : value;
};
const computeUnitTotal = (qty: string, unitPrice: string): string => {
  const q = Number(qty);
  const p = Number(unitPrice);
  if (!Number.isFinite(q) || !Number.isFinite(p)) return '';
  const total = q * p;
  if (!Number.isFinite(total)) return '';
  return toFixed2String(total);
};
const computeFinalTotal = (unitTotal: string, discount: string, vat: string): string => {
  const u = Number(unitTotal);
  const d = Number(discount || '0');
  const v = Number(vat || '0');
  if (!Number.isFinite(u) || !Number.isFinite(d) || !Number.isFinite(v)) return '';
  const total = u - d + v;
  if (!Number.isFinite(total)) return '';
  return toFixed2String(total);
};
const computeCurrencyTotal = (lineTotal: string, rate: string): string => {
  const t = Number(lineTotal);
  const r = Number(rate);
  if (!Number.isFinite(t) || !Number.isFinite(r)) return '';
  const total = t * r;
  if (!Number.isFinite(total)) return '';
  return toFixed2String(total);
};
const asFinite = (value: string): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const fmt2 = (n: number): string =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const Form: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editMatch = matchPath(
    { path: '/spares/spare-purchase-orders/:id/edit', end: true },
    location.pathname,
  );
  const isEdit = editMatch != null;
  const rawId = editMatch?.params.id;
  const purchaseOrderId =
    rawId != null && /^\d+$/.test(String(rawId)) ? Number(rawId) : undefined;
  const paramQuotationId = React.useMemo(() => {
    const e = searchParams.get('quotationId');
    const n = e != null ? Number(e) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
  }, [searchParams]);
  const selectedItemIdsFromQuery = React.useMemo(() => {
    const raw = searchParams.get('selectedItemIds');
    if (!raw) return [] as number[];
    return raw
      .split(',')
      .map((v) => Number(v.trim()))
      .filter((v) => Number.isFinite(v) && v > 0);
  }, [searchParams]);

  const { data: existing, isLoading: loadingExisting } = useSparePurchaseOrder(purchaseOrderId);
  const { data: units = [] } = useSWR<any[]>('/spare-items', fetcher);
  const { data: quotationsRes } = useSWR<any>('/spare-supplier-quotations?page=1&limit=500', fetcher);
  const { data: currenciesRes } = useSWR<any>('/currencies?page=1&limit=500', fetcher);
  const quotations = React.useMemo(
    () => (Array.isArray(quotationsRes) ? quotationsRes : quotationsRes?.data ?? []),
    [quotationsRes],
  );

  const [selectedQuotationId, setSelectedQuotationId] = React.useState('');
  const { data: selectedQuotation } = useSWR<any>(
    selectedQuotationId ? `/spare-supplier-quotations/${selectedQuotationId}` : null,
    fetcher,
  );

  const [isSaving, setIsSaving] = React.useState(false);
  const [formData, setFormData] = React.useState({
    orderDate: '',
    ref: '',
    notes: '',
    paymentTerms: '',
    currencyId: '',
    exchangeRate: '',
    exchangeDate: '',
  });
  const [lines, setLines] = React.useState<LineForm[]>([newLine()]);
  const [expenses, setExpenses] = React.useState<ExpenseForm[]>([newExpense()]);
  const [bulkDiscountPct, setBulkDiscountPct] = React.useState('');
  const [bulkVatPct, setBulkVatPct] = React.useState('');

  const { data: itemsRes } = useSWR<any>('/spare-items?page=1&limit=200', fetcher);
  const items = React.useMemo(
    () => (Array.isArray(itemsRes) ? itemsRes : itemsRes?.data ?? []),
    [itemsRes],
  );
  const selectedLevel1Values = React.useMemo(
    () =>
      Array.from(
        new Set(
          lines
            .map((line) => line.level1Filter?.trim())
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [lines],
  );
  const { data: levelItemsMap = {} } = useSWR<Record<string, any[]>>(
    selectedLevel1Values.length > 0 ? ['po-line-level-items', ...selectedLevel1Values] : null,
    async () => {
      const entries = await Promise.all(
        selectedLevel1Values.map(async (level1) => {
          const params = new URLSearchParams();
          params.set('filters', JSON.stringify({ filters: { level1: [level1] } }));
          const response = await fetcher(`/spare-items?${params.toString()}`);
          const levelItems = Array.isArray(response) ? response : response?.data ?? [];
          return [level1, levelItems] as const;
        }),
      );
      return Object.fromEntries(entries);
    },
    { revalidateOnFocus: false },
  );
  const itemsById = React.useMemo(() => {
    const map = new Map<string, any>();
    for (const item of items) {
      map.set(String(item.id), item);
    }
    for (const levelItems of Object.values(levelItemsMap)) {
      for (const item of levelItems) {
        map.set(String(item.id), item);
      }
    }
    return map;
  }, [items, levelItemsMap]);
  const level1Options = React.useMemo(() => {
    const set = new Set<string>();
    for (const it of items) {
      const L = (it.level1 ?? '').trim();
      if (L) set.add(L);
    }
    return [
      { value: '', label: 'All' },
      ...Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v })),
    ];
  }, [items]);

  const quotationOptions = React.useMemo(() => {
    const rows = quotations.map((q: any) => ({
      value: String(q.id),
      label: `${q.ref || `QUO-${q.id}`}${q.supplier?.name ? ` · ${q.supplier.name}` : ''}`,
    }));
    if (
      existing?.quotationId &&
      existing?.quotation &&
      !rows.some((o: { value: string }) => o.value === String(existing.quotationId))
    ) {
      rows.push({
        value: String(existing.quotationId),
        label: existing.quotation.ref || `QUO-${existing.quotationId}`,
      });
    }
    return [{ value: '', label: 'Select quotation' }, ...rows];
  }, [quotations, existing?.quotation, existing?.quotationId]);

  const currencies = React.useMemo(
    () => (Array.isArray(currenciesRes) ? currenciesRes : currenciesRes?.data ?? []) as {
      id: number;
      code: string;
      name: string;
      symbol?: string | null;
      isActive?: boolean;
    }[],
    [currenciesRes],
  );

  const currencyOptions = React.useMemo(() => {
    const active = currencies.filter((c) => c.isActive !== false);
    const rows = active.map((c) => ({
      value: String(c.id),
      label: `${c.name} (${c.code})`,
    }));
    if (existing?.currencyId && existing?.currency && !rows.some((o) => o.value === String(existing.currencyId))) {
      rows.push({
        value: String(existing.currencyId),
        label: `${existing.currency.name} (${existing.currency.code})`,
      });
    }
    return [{ value: '', label: 'Currency (optional)' }, ...rows];
  }, [currencies, existing?.currency, existing?.currencyId]);
  const currencyTotalCaption = React.useMemo(() => {
    const selectedId = formData.currencyId ? Number(formData.currencyId) : NaN;
    const selected = Number.isFinite(selectedId) ? currencies.find((c) => c.id === selectedId) : undefined;
    const cur = selected ?? existing?.currency;
    if (cur?.name?.trim()) {
      const code = (cur.code ?? '').trim();
      const sym = (cur.symbol ?? '').trim();
      if (code) {
        return sym ? `${cur.name} (${code}) ${sym}` : `${cur.name} (${code})`;
      }
      return sym ? `${cur.name} ${sym}` : cur.name.trim();
    }
    const symbol = (selected?.symbol ?? existing?.currency?.symbol ?? '').trim();
    return symbol ? `(${symbol}) Total` : 'Total';
  }, [currencies, formData.currencyId, existing?.currency]);

  const getItemOptionsForLine = React.useCallback(
    (line: LineForm) => {
      const f = line.level1Filter?.trim();
      const filtered = f ? levelItemsMap[f] ?? [] : items;
      const rows: { value: string; label: string }[] = filtered.map(
        (it: { id: number; vendorCode?: string; name?: string; unit?: { ucode?: string } }) => ({
          value: String(it.id),
          label: `${it.name || ''}`,
        }),
      );
      if (line.itemId && !rows.some((o) => o.value === line.itemId)) {
        const idStr = String(line.itemId);
        const label = (line.itemDescr && line.itemDescr.trim()) ? line.itemDescr.trim() : `Item #${idStr}`;
        rows.push({ value: idStr, label });
      }
      return [{ value: '', label: 'Select item' }, ...rows];
    },
    [items, levelItemsMap],
  );

  const unitOptions = React.useMemo(() => {
    const rows: { value: string; label: string }[] = units.map((unit) => ({
      value: String(unit.id),
      label: unit.ucode || unit.descr || `Unit #${unit.id}`,
    }));
    const seen = new Set(rows.map((o) => o.value));
    for (const line of lines) {
      if (!String(line.unitId).trim()) continue;
      const uStr = String(line.unitId);
      if (seen.has(uStr)) continue;
      seen.add(uStr);
      rows.push({ value: uStr, label: `Unit #${uStr}` });
    }
    return [{ value: '', label: 'Unit' }, ...rows];
  }, [units, lines]);

  React.useEffect(() => {
    if (!isEdit && paramQuotationId) setSelectedQuotationId(String(paramQuotationId));
  }, [isEdit, paramQuotationId]);

  React.useEffect(() => {
    if (!existing) return;
    setSelectedQuotationId(String(existing.quotationId));
    setFormData({
      orderDate: existing.orderDate ? existing.orderDate.slice(0, 10) : '',
      ref: existing.ref || '',
      notes: existing.notes || '',
      paymentTerms: existing.paymentTerms || '',
      currencyId: existing.currencyId != null ? String(existing.currencyId) : '',
      exchangeRate: existing.exchangeRate != null ? String(existing.exchangeRate) : '',
      exchangeDate: existing.exchangeDate ? existing.exchangeDate.slice(0, 10) : '',
    });
    const mapped = (existing.lineItems || []).map((line) => ({
      key: `line-${line.id || Math.random()}`,
      level1Filter: (line.item?.level1 ?? '').trim(),
      itemId: line.itemId ? String(line.itemId) : '',
      itemVcode: line.itemVcode || '',
      itemDescr: line.itemDescr || '',
      unitId: line.unitId ? String(line.unitId) : '',
      qty: line.qty != null ? String(line.qty) : '',
      unitPrice: line.unitPrice != null ? String(line.unitPrice) : '',
      unitTotal: computeUnitTotal(
        line.qty != null ? String(line.qty) : '',
        line.unitPrice != null ? String(line.unitPrice) : '',
      ),
      lineTotal:
        line.lineTotal != null
          ? String(line.lineTotal)
          : computeFinalTotal(
              computeUnitTotal(
                line.qty != null ? String(line.qty) : '',
                line.unitPrice != null ? String(line.unitPrice) : '',
              ),
              line.discount != null ? String(line.discount) : '',
              line.vat != null ? String(line.vat) : '',
            ),
      discount: line.discount != null ? String(line.discount) : '',
      vat: line.vat != null ? String(line.vat) : '',
      quotationQty: line.quotationQty != null ? String(line.quotationQty) : '',
      itemNotes: line.itemNotes || '',
    }));
    setLines(mapped.length > 0 ? mapped : [newLine()]);
    const expMapped = (existing.expenses || []).map((exp) => ({
      key: `exp-${exp.id || Math.random()}`,
      description: exp.description || '',
      value: exp.value != null ? String(exp.value) : '',
      vat: exp.vat != null ? String(exp.vat) : '',
    }));
    setExpenses(expMapped.length > 0 ? expMapped : [newExpense()]);
  }, [existing]);

  const copyLinesFromQuotation = React.useCallback(() => {
    if (!selectedQuotation || !Array.isArray(selectedQuotation.lineItems)) return;
    const selectedSet = new Set(selectedItemIdsFromQuery);
    const sourceLines = selectedSet.size
      ? selectedQuotation.lineItems.filter((line: { itemId?: number }) => line.itemId != null && selectedSet.has(Number(line.itemId)))
      : selectedQuotation.lineItems;
    const mapped = sourceLines.map((line: { id?: number; itemId?: number; itemVcode?: string; itemDescr?: string; unitId?: number; qty?: number; unitPrice?: unknown; lineTotal?: unknown; discount?: unknown; vat?: unknown; requisitionQty?: number; enquiryQty?: number; itemNotes?: string; item?: { level1?: string | null } | null }) => ({
      key: `line-${line.id || Math.random()}`,
      level1Filter: (line.item?.level1 ?? '').trim(),
      itemId: line.itemId ? String(line.itemId) : '',
      itemVcode: line.itemVcode || '',
      itemDescr: line.itemDescr || '',
      unitId: line.unitId ? String(line.unitId) : '',
      qty: line.qty != null ? String(line.qty) : '',
      unitPrice: line.unitPrice != null ? String(line.unitPrice) : '',
      unitTotal: computeUnitTotal(
        line.qty != null ? String(line.qty) : '',
        line.unitPrice != null ? String(line.unitPrice) : '',
      ),
      lineTotal:
        line.lineTotal != null
          ? String(line.lineTotal)
          : computeFinalTotal(
              computeUnitTotal(
                line.qty != null ? String(line.qty) : '',
                line.unitPrice != null ? String(line.unitPrice) : '',
              ),
              line.discount != null ? String(line.discount) : '',
              line.vat != null ? String(line.vat) : '',
            ),
      discount: line.discount != null ? String(line.discount) : '',
      vat: line.vat != null ? String(line.vat) : '',
      quotationQty: line.qty != null ? String(line.qty) : '',
      itemNotes: line.itemNotes || '',
    }));
    setLines(mapped.length > 0 ? mapped : [newLine()]);
  }, [selectedQuotation, selectedItemIdsFromQuery]);
  const selectedItemsPrefilledRef = React.useRef(false);
  React.useEffect(() => {
    if (isEdit || selectedItemsPrefilledRef.current) return;
    if (!paramQuotationId) return;
    if (!selectedQuotationId || Number(selectedQuotationId) !== paramQuotationId) return;
    if (!selectedQuotation || !Array.isArray(selectedQuotation.lineItems)) return;
    copyLinesFromQuotation();
    selectedItemsPrefilledRef.current = true;
  }, [isEdit, paramQuotationId, selectedQuotationId, selectedQuotation, copyLinesFromQuotation]);

  const onItemChange = (lineKey: string, itemId: string) => {
    const item = itemsById.get(itemId) as
      | { id: number; vendorCode?: string; name?: string; unitId?: number }
      | undefined;
    setLines((prev) =>
      prev.map((line) =>
        line.key !== lineKey
          ? line
          : {
              ...line,
              itemId,
              itemVcode: item?.vendorCode || '',
              itemDescr: item?.name || '',
              unitId: item?.unitId ? String(item.unitId) : '',
              level1Filter: ((item as any)?.level1 ?? '').trim(),
              unitTotal: '',
              lineTotal: '',
              discount: '',
              vat: '',
            },
      ),
    );
  };
  const onLevel1FilterChange = (lineKey: string, value: string) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.key !== lineKey) return line;
        const next: LineForm = { ...line, level1Filter: value };
        const f = value.trim();
        const previousLevel1 = (line.level1Filter ?? '').trim();
        if (f && line.itemId && previousLevel1 !== f) {
          next.itemId = '';
          next.itemDescr = '';
          next.itemVcode = '';
          next.unitId = '';
        }
        return next;
      }),
    );
  };

  const normalizedLines: SparePurchaseOrderLine[] = React.useMemo(() => {
    return lines
      .map((line) => ({
        itemId: asNumber(line.itemId),
        itemVcode: line.itemVcode || undefined,
        itemDescr: line.itemDescr || undefined,
        unitId: asNumber(line.unitId),
        qty: asNumber(line.qty),
        unitPrice: asNumber(line.unitPrice),
        lineTotal: asNumber(line.lineTotal),
        discount: asNumber(line.discount),
        vat: asNumber(line.vat),
        quotationQty: asNumber(line.quotationQty),
        itemNotes: line.itemNotes || undefined,
      }))
      .filter(
        (line) =>
          line.itemId != null ||
          line.itemDescr ||
          line.qty != null ||
          line.quotationQty != null ||
          line.itemNotes ||
          line.discount != null ||
          line.vat != null,
      );
  }, [lines]);

  const lineSums = React.useMemo(() => {
    return lines.reduce(
      (acc, line) => {
        acc.unitTotal += asFinite(line.unitTotal);
        acc.discount += asFinite(line.discount);
        acc.vat += asFinite(line.vat);
        acc.total += asFinite(line.lineTotal);
        acc.currencyTotal += asFinite(computeCurrencyTotal(line.lineTotal, formData.exchangeRate));
        return acc;
      },
      { unitTotal: 0, discount: 0, vat: 0, total: 0, currencyTotal: 0 },
    );
  }, [lines, formData.exchangeRate]);

  const normalizedExpenses: SparePurchaseOrderExpense[] = React.useMemo(() => {
    return expenses
      .map((row) => ({
        description: row.description.trim() || undefined,
        value: asNumber(row.value),
        vat: asNumber(row.vat),
      }))
      .filter((row) => row.description || row.value != null || row.vat != null);
  }, [expenses]);

  const returnPath = React.useMemo(() => getLocationReturnPath(location.state), [location.state]);
  const goBackFallback =
    isEdit && purchaseOrderId != null
      ? moduleConfig.module.route.detail(purchaseOrderId)
      : moduleConfig.module.route.list;
  const goBack = useNavigateBack(goBackFallback, returnPath);

  const submit = async () => {
    const quotationId = asNumber(selectedQuotationId);
    const hasRequiredDetails =
      Boolean(formData.orderDate.trim()) &&
      Boolean(formData.ref.trim()) &&
      Boolean(formData.currencyId.trim()) &&
      asNumber(formData.exchangeRate) != null &&
      Boolean(formData.exchangeDate.trim());
    if (!quotationId || !hasRequiredDetails) return;
    setIsSaving(true);
    try {
      const payload = {
        orderDate: formData.orderDate || undefined,
        quotationId,
        ref: formData.ref || undefined,
        notes: formData.notes || undefined,
        paymentTerms: formData.paymentTerms || undefined,
        currencyId: asNumber(formData.currencyId),
        exchangeRate: asNumber(formData.exchangeRate),
        exchangeDate: formData.exchangeDate || undefined,
        lineItems: normalizedLines,
        expenses: normalizedExpenses,
      };
      if (isEdit && purchaseOrderId) {
        await updateSparePurchaseOrder(purchaseOrderId, payload);
        navigateBackOrFallback(navigate, {
          explicitReturnPath: returnPath,
          fallbackPath: moduleConfig.module.route.detail(purchaseOrderId),
        });
      } else {
        await createSparePurchaseOrder(payload);
        navigate(moduleConfig.module.route.list);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const hasRequiredOrderDetails =
    Boolean(formData.orderDate.trim()) &&
    Boolean(formData.ref.trim()) &&
    Boolean(formData.currencyId.trim()) &&
    asNumber(formData.exchangeRate) != null &&
    Boolean(formData.exchangeDate.trim());
  const purchaseOrderCaseCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const caseIdN = existing?.caseId ?? null;
    if (caseIdN == null || caseIdN <= 0) return null;
    return (
      formatCaseParenthetical(
        existing?.spareCase ?? {
          id: caseIdN,
        },
        (iso) => formatDateGlobal(iso || undefined),
      ) ?? null
    );
  }, [isEdit, existing?.caseId, existing?.spareCase]);
  const purchaseOrderQuotationCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const ref = existing?.quotation?.ref?.trim() || (existing?.quotationId != null ? `Q-${existing.quotationId}` : '');
    if (!ref) return null;
    const date = existing?.quotation?.quoDate ? formatDateGlobal(existing.quotation.quoDate) : '';
    return `Quotation: ${ref}${date ? ` - ${date}` : ''}`;
  }, [isEdit, existing?.quotation?.ref, existing?.quotation?.quoDate, existing?.quotationId]);
  const shouldLockQuotationLineFields = React.useCallback(
    (line: LineForm) => {
      if (!isEdit) return false;
      const caseIdN = existing?.caseId ?? null;
      const quotationIdN = existing?.quotationId ?? null;
      if (caseIdN == null || caseIdN <= 0) return false;
      if (quotationIdN == null || quotationIdN <= 0) return false;
      // Quotation-origin rows carry quotation quantity on this form.
      return line.quotationQty !== '';
    },
    [isEdit, existing?.caseId, existing?.quotationId],
  );

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <Card variant="outlined" className={cn('flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5', 'border shadow-sm')}>
        <Button type="button" variant="ghost" size="sm" className={cn('-ml-2 gap-1.5 font-medium', colors.text)} onClick={goBack}>
          <ChevronLeftIcon className="h-4 w-4 opacity-80" />
          Back
        </Button>
        <Button
          type="submit"
          disabled={isSaving || loadingExisting || !asNumber(selectedQuotationId) || !hasRequiredOrderDetails}
          loading={isSaving}
          size="default"
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </Card>

      {purchaseOrderCaseCaption ? (
        <Callout variant="success" title="Linked spare case">
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm',
                    'border-green-200/90 bg-green-100/70 text-green-950 dark:border-green-700/60 dark:bg-green-900/40 dark:text-green-100',
                  )}
                >
                  {purchaseOrderCaseCaption}
                </span>
                {purchaseOrderQuotationCaption ? (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm font-medium',
                      'border-indigo-200/90 bg-indigo-50/90 text-indigo-900 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-200',
                    )}
                  >
                    {purchaseOrderQuotationCaption}
                  </span>
                ) : null}
              </div>
            </Callout>
      ) : null}

      <Card variant="default" className="p-4 md:p-5">
        <div className="mb-4">
          <h2 className={cn('text-sm font-semibold uppercase tracking-wide', colors.textSecondary)}>Order details</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
          <div className="md:col-span-3">
            <Label htmlFor="po-date" required>Order date</Label>
            <DateInput id="po-date" value={formData.orderDate} onChange={(e) => setFormData((s) => ({ ...s, orderDate: e.target.value }))} placeholder="Select date" showFormatHint={false} />
          </div>
          {!isEdit ? (
            <div className="md:col-span-3">
              <Label htmlFor="po-quotation">Quotation</Label>
              <Select
                id="po-quotation"
                value={selectedQuotationId}
                onChange={(e) => setSelectedQuotationId(e.target.value)}
                options={quotationOptions}
                placeholder="Select quotation"
                showClearButton={false}
              />
            </div>
          ) : null}
          <div className="md:col-span-2">
            <Label htmlFor="po-ref" required>Reference</Label>
            <TextInput id="po-ref" value={formData.ref} onChange={(e) => setFormData((s) => ({ ...s, ref: e.target.value }))} placeholder="Reference" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="po-currency" required>Currency</Label>
            <Select
              id="po-currency"
              value={formData.currencyId}
              onChange={(e) => setFormData((s) => ({ ...s, currencyId: e.target.value }))}
              options={currencyOptions}
              placeholder="Select currency…"
              showClearButton={false}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="po-ex-rate" required>Exchange rate</Label>
            <NumberInput
              id="po-ex-rate"
              value={formData.exchangeRate}
              onChange={(e) => setFormData((s) => ({ ...s, exchangeRate: e.target.value == null ? '' : String(e.target.value) }))}
              showClearButton={false}
              placeholder="Rate"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="po-ex-date" required>Exchange date</Label>
            <DateInput
              id="po-ex-date"
              value={formData.exchangeDate}
              onChange={(e) => setFormData((s) => ({ ...s, exchangeDate: e.target.value }))}
              placeholder="Date"
              showFormatHint={false}
            />
          </div>
          <div className="md:col-span-5">
            <Label htmlFor="po-payment-terms">Payment terms</Label>
            <TextInput
              id="po-payment-terms"
              value={formData.paymentTerms}
              onChange={(e) => setFormData((s) => ({ ...s, paymentTerms: e.target.value }))}
              placeholder="Payment terms"
            />
          </div>
          <div className="md:col-span-4">
            <Label htmlFor="po-notes">Notes</Label>
            <Textarea id="po-notes" value={formData.notes} onChange={(e) => setFormData((s) => ({ ...s, notes: e.target.value }))} rows={2} showClearButton={false} className="min-h-[72px]" />
          </div>
          {!isEdit ? (
            <div className="md:col-span-12">
              <div className={cn('rounded-lg border p-3 text-sm', colors.border, colors.surfaceHover)}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className={cn('font-medium', colors.text)}>Copy lines from selected quotation</span>
                  <Button type="button" variant="outline" size="sm" disabled={!selectedQuotationId || !selectedQuotation} onClick={copyLinesFromQuotation} className="gap-1.5">
                    <RefreshIcon className="h-4 w-4" />
                    Copy items
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </Card>

      <Card variant="default" className="p-4 md:p-5">
        <Accordion className="space-y-3">
          <AccordionItem
            title="Expenses"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mb-3 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="icon"
                title="Add expense"
                aria-label="Add expense"
                onClick={() => setExpenses((prev) => [...prev, newExpense()])}
              >
                <PlusIcon className="h-5 w-5" />
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={cn('border-b', colors.border)}>
                    <th className="min-w-[10rem] px-2 py-2 text-left text-xs font-medium">Description</th>
                    <th className="w-28 px-2 py-2 text-left text-xs font-medium">Value</th>
                    <th className="w-28 px-2 py-2 text-left text-xs font-medium">VAT</th>
                    <th className="px-2 py-2 text-right text-xs font-medium"> </th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((row) => (
                    <tr key={row.key} className="align-top">
                      <td className="p-2">
                        <TextInput
                          size="sm"
                          value={row.description}
                          onChange={(e) =>
                            setExpenses((prev) => prev.map((r) => (r.key === row.key ? { ...r, description: e.target.value } : r)))
                          }
                          showClearButton={false}
                          placeholder="Description"
                        />
                      </td>
                      <td className="p-2">
                        <NumberInput
                          size="sm"
                          value={row.value}
                          onChange={(e) =>
                            setExpenses((prev) =>
                              prev.map((r) => (r.key === row.key ? { ...r, value: e.target.value == null ? '' : String(e.target.value) } : r)),
                            )
                          }
                          showClearButton={false}
                          placeholder="Value"
                        />
                      </td>
                      <td className="p-2">
                        <NumberInput
                          size="sm"
                          value={row.vat}
                          onChange={(e) =>
                            setExpenses((prev) =>
                              prev.map((r) => (r.key === row.key ? { ...r, vat: e.target.value == null ? '' : String(e.target.value) } : r)),
                            )
                          }
                          showClearButton={false}
                          placeholder="VAT"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Button
                          type="button"
                          variant="danger"
                          size="icon"
                          onClick={() => setExpenses((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== row.key) : [newExpense()]))}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AccordionItem>

          <AccordionItem
            title="Lines"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className={cn('flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm', colors.border)}>
                  <span className={cn('text-xs font-medium', colors.textSecondary)}>Apply Item Discount (%)</span>
                  <NumberInput
                    size="sm"
                    value={bulkDiscountPct}
                    onChange={(e) => setBulkDiscountPct(e.target.value == null ? '' : String(e.target.value))}
                    placeholder="%"
                    showClearButton={false}
                    className="h-9 min-h-[2.25rem] w-24 box-border py-1.5 leading-normal"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const pct = Number(bulkDiscountPct);
                      if (!Number.isFinite(pct)) return;
                      setLines((prev) =>
                        prev.map((r) => {
                          const unit = Number(r.unitTotal);
                          const discountValue = Number.isFinite(unit) ? toFixed2String((unit * pct) / 100) : '';
                          return {
                            ...r,
                            discount: discountValue,
                            lineTotal: computeFinalTotal(r.unitTotal, discountValue, r.vat),
                          };
                        }),
                      );
                    }}
                    disabled={!lines.length}
                  >
                    Apply
                  </Button>
                </div>
                <div className={cn('flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm', colors.border)}>
                  <span className={cn('text-xs font-medium', colors.textSecondary)}>Apply Item VAT (%)</span>
                  <NumberInput
                    size="sm"
                    value={bulkVatPct}
                    onChange={(e) => setBulkVatPct(e.target.value == null ? '' : String(e.target.value))}
                    placeholder="%"
                    showClearButton={false}
                    className="h-9 min-h-[2.25rem] w-24 box-border py-1.5 leading-normal"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const pct = Number(bulkVatPct);
                      if (!Number.isFinite(pct)) return;
                      setLines((prev) =>
                        prev.map((r) => {
                          const unit = Number(r.unitTotal);
                          const vatValue = Number.isFinite(unit) ? toFixed2String((unit * pct) / 100) : '';
                          return {
                            ...r,
                            vat: vatValue,
                            lineTotal: computeFinalTotal(r.unitTotal, r.discount, vatValue),
                          };
                        }),
                      );
                    }}
                    disabled={!lines.length}
                  >
                    Apply
                  </Button>
                </div>
              </div>
              <Button type="button" variant="outline" size="icon" title="Add line" aria-label="Add line" onClick={() => setLines((prev) => [...prev, newLine()])}>
                <PlusIcon className="h-5 w-5" />
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={cn('border-b', colors.border)}>
                    <th className="w-[10rem] min-w-[8rem] px-2 py-2 text-left text-xs font-medium">Level 1</th>
                    <th className="min-w-[12rem] px-2 py-2 text-left text-xs font-medium">Item</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right text-xs font-medium">Unit</th>
                    <th className="w-20 px-2 py-2 text-right text-xs font-medium">Quo qty</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Qty</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Price</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Unit Total</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Discount</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">VAT</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Total</th>
                    <th className="w-28 px-2 py-2 text-right text-xs font-medium">{currencyTotalCaption}</th>
                    <th className="w-[10rem] px-2 py-2 text-left text-xs font-medium">Notes</th>
                    <th className="px-2 py-2 text-right text-xs font-medium"> </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => {
                    const lockQuotationFields = shouldLockQuotationLineFields(line);
                    return (
                    <tr key={line.key} className="align-top">
                      <td className="p-2">
                        <Select
                          name={`po-line-l1-${line.key}`}
                          value={line.level1Filter}
                          onChange={(e) => onLevel1FilterChange(line.key, e.target.value)}
                          options={level1Options}
                          placeholder="Level 1"
                          showClearButton={false}
                          className="h-10 min-h-[2.5rem] w-full text-sm"
                          disabled={lockQuotationFields}
                        />
                      </td>
                      <td className="p-2">
                        <Select
                          value={line.itemId}
                          onChange={(e) => onItemChange(line.key, e.target.value)}
                          options={getItemOptionsForLine(line)}
                          placeholder="Select item"
                          showClearButton={false}
                          className="h-10 min-h-[2.5rem] w-full min-w-[12rem] text-sm"
                          disabled={lockQuotationFields}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Select
                          value={line.unitId}
                          onChange={(e) => setLines((prev) => prev.map((r) => (r.key === line.key ? { ...r, unitId: e.target.value } : r)))}
                          options={unitOptions}
                          placeholder="Unit"
                          showClearButton={false}
                          className="h-10 min-h-[2.5rem] text-right"
                          disabled={lockQuotationFields}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.quotationQty}
                          disabled
                          showClearButton={false}
                          placeholder="Quo"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.qty}
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                const qtyValue = e.target.value == null ? '' : String(e.target.value);
                                const unitTotal = computeUnitTotal(qtyValue, r.unitPrice);
                                return {
                                  ...r,
                                  qty: qtyValue,
                                  unitTotal,
                                  lineTotal: computeFinalTotal(unitTotal, r.discount, r.vat),
                                };
                              }),
                            )
                          }
                          showClearButton={false}
                          placeholder="Qty"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.unitPrice}
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                const priceValue = e.target.value == null ? '' : String(e.target.value);
                                const unitTotal = computeUnitTotal(r.qty, priceValue);
                                return {
                                  ...r,
                                  unitPrice: priceValue,
                                  unitTotal,
                                  lineTotal: computeFinalTotal(unitTotal, r.discount, r.vat),
                                };
                              }),
                            )
                          }
                          showClearButton={false}
                          placeholder="Price"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.unitTotal}
                          disabled
                          showClearButton={false}
                          placeholder="Unit Total"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.discount}
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                const discountValue = e.target.value == null ? '' : String(e.target.value);
                                return {
                                  ...r,
                                  discount: discountValue,
                                  lineTotal: computeFinalTotal(r.unitTotal, discountValue, r.vat),
                                };
                              }),
                            )
                          }
                          showClearButton={false}
                          placeholder="Discount"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.vat}
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                const vatValue = e.target.value == null ? '' : String(e.target.value);
                                return {
                                  ...r,
                                  vat: vatValue,
                                  lineTotal: computeFinalTotal(r.unitTotal, r.discount, vatValue),
                                };
                              }),
                            )
                          }
                          showClearButton={false}
                          placeholder="VAT"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.lineTotal}
                          disabled
                          showClearButton={false}
                          placeholder="Total"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={computeCurrencyTotal(line.lineTotal, formData.exchangeRate)}
                          disabled
                          showClearButton={false}
                          placeholder={currencyTotalCaption}
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2">
                        <TextInput
                          size="sm"
                          value={line.itemNotes}
                          onChange={(e) => setLines((prev) => prev.map((r) => (r.key === line.key ? { ...r, itemNotes: e.target.value } : r)))}
                          showClearButton={false}
                          placeholder="Notes"
                          className="h-10 min-h-[2.5rem]"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Button
                          type="button"
                          variant="danger"
                          size="icon"
                          onClick={() => setLines((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== line.key) : [newLine()]))}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </Button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className={cn('border-t', colors.border)}>
                    <td colSpan={6} className={cn('px-2 py-2 text-right text-xs font-semibold', colors.textSecondary)}>
                      SUM
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Unit Total SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.unitTotal)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Discount SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.discount)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>VAT SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.vat)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>TOTAL SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.total)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>{currencyTotalCaption} SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.currencyTotal)}</div>
                    </td>
                    <td />
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </AccordionItem>
        </Accordion>
      </Card>
    </form>
  );
};

export default Form;
